import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/vue-query"
import { z } from "zod"
import type { AxiosError } from "axios"
import * as dt from "date-fns"
import NProgress from "nprogress"

import { dispatchFileResponses } from "../utils/files"
import { defaultRetry } from "~/plugins/vue-query"

import {
  DataFrame,
  DocumentSchemaShort,
  InvoiceCancelActions,
  InvoiceSchemaShort,
  NotifLevel,
  ProducerCompanyKind,
  SiteSchema,
} from "#imports"
import type { ContractSchema } from "#imports"
import type { GSRefreshParams } from "~/composables/codegen/api-client"

import { useAxiosInstance, type InferGetInput, type InferMutateInput } from "~/composables/api"
import { trackMutation } from "~/composables/analytics"

export type GSFinanceTSResponse = {
  clause_product?: DataFrame
  status?: DataFrame
  status_price_mechanism?: DataFrame
  price_mechanism?: DataFrame
  last_year: DataFrame
  budget?: DataFrame
}

export { useQueryClient }

export const MutationTypes = z.enum([
  "computeInvoice",
  "createPaymentSchedule",
  "createRegulation",
  "submitInvoice",
  "publishInvoice",
  "updateInvoice",
  "updateContract",
  "updateClause",
  "updatePortfolio",
  "updateDeliveryPoints",
  "createDPContractualMapping",
  "updateDPContractualMapping",
  "deleteDPContractualMapping",
  "updateEvent",
  "postEvent",
  "deleteEvent",
  "refreshDatahealth",
  "refreshModelDatahealth",
  //
  "createSiteStatus",
  "updateSiteStatus",
  "deleteSiteStatus",
  //
  "askBackfill",
  "updateTSData",
  "updateTSMetadata",
  "askIntraday",
  "askEnedisStaticData",
  "refreshGSFinance",
  //
  "deleteDocument",
  "updateSupplyInvoice",
  "docgenOrder",
  "uploadDocument",
  "docgenRefresh",
  //
  "cancelInvoice",
  "setInvoiceDefinitiveStatus",
  "setInvoiceAccounted",
  "publish_invoicing_request",
  //
  "createNote",
  "updateNote",
  "deleteNote",
  "archiveNote",
  //
  "updateLegalEntity",
  "updateLegalEntityAccountingInfos",
  //
  "createAddress",
  "updateAddress",
  //
  "createContact",
  "updateContact",
  //
  "updateInvoiceLine",
  "setInvoiceExtra",
  //
  "runPerformanceTest",
  "runSiteHandOver",
])
export type MutationTypes = z.infer<typeof MutationTypes>

const mutationOptions: Record<
  MutationTypes,
  { title: string; enterText?: string; errorText?: string; successText: string }
> = {
  [MutationTypes.enum.computeInvoice]: {
    title: "computing_invoices",
    enterText: "minute",
    errorText: "computing_failed",
    successText: "computing_success",
  },
  [MutationTypes.enum.createPaymentSchedule]: {
    title: "creating_payment_schedule",
    enterText: "minute",
    errorText: "payment_schedule_failed",
    successText: "payment_schedule_success",
  },
  [MutationTypes.enum.createRegulation]: {
    title: "creating_regulation",
    enterText: "minute",
    errorText: "regulation_failed",
    successText: "regulation_success",
  },
  [MutationTypes.enum.submitInvoice]: {
    title: "submitting_invoices",
    enterText: "minute",
    errorText: "submitting_failed",
    successText: "submitting_success",
  },
  [MutationTypes.enum.publishInvoice]: {
    title: "publishing_invoices",
    enterText: "minute",
    errorText: "submitting_failed",
    successText: "submitting_success",
  },
  [MutationTypes.enum.updateInvoice]: {
    title: "update_invoice",
    enterText: "minute",
    errorText: "update_failed",
    successText: "update_success",
  },
  [MutationTypes.enum.updateContract]: {
    title: "update_contract",
    errorText: "update_contract_failed",
    successText: "update_contract_success",
  },
  [MutationTypes.enum.updateClause]: {
    title: "update_clause",
    errorText: "update_clause_failed",
    successText: "update_clause_success",
  },
  [MutationTypes.enum.updatePortfolio]: {
    title: "update_portfolio",
    enterText: "minute",
    errorText: "update_portfolio_failed",
    successText: "update_portfolio_success",
  },
  [MutationTypes.enum.updateDeliveryPoints]: {
    title: "update_dp",
    enterText: "minute",
    errorText: "update_dp_failed",
    successText: "update_dp_success",
  },
  [MutationTypes.enum.createDPContractualMapping]: {
    title: "create_dp_contractual_mapping",
    errorText: "create_dp_contractual_mapping_failed",
    successText: "create_dp_contractual_mapping_success",
  },
  [MutationTypes.enum.updateDPContractualMapping]: {
    title: "update_dp_contractual_mapping",
    errorText: "update_dp_contractual_mapping_failed",
    successText: "update_dp_contractual_mapping_success",
  },
  [MutationTypes.enum.deleteDPContractualMapping]: {
    title: "delete_dp_contractual_mapping",
    errorText: "delete_dp_contractual_mapping_failed",
    successText: "delete_dp_contractual_mapping_success",
  },
  [MutationTypes.enum.createSiteStatus]: {
    title: "log_site_status",
    enterText: "minute",
    errorText: "create_site_status_failed",
    successText: "create_site_status_success",
  },
  [MutationTypes.enum.updateSiteStatus]: {
    title: "edit_site_status",
    enterText: "minute",
    errorText: "update_site_status_failed",
    successText: "update_site_status_success",
  },
  [MutationTypes.enum.deleteSiteStatus]: {
    title: "delete_site_status",
    enterText: "minute",
    errorText: "delete_site_status_failed",
    successText: "delete_site_status_success",
  },
  // Timeseries
  [MutationTypes.enum.askBackfill]: {
    title: "ask_backfill",
    enterText: "minute",
    errorText: "ask_backfill_failed",
    successText: "ask_backfill_success",
  },
  [MutationTypes.enum.updateTSData]: {
    title: "update_ts_data",
    enterText: "update_ts_data_sent",
    errorText: "update_ts_data_error",
    successText: "update_ts_data_success",
  },
  [MutationTypes.enum.updateTSMetadata]: {
    title: "update_ts_metadata",
    errorText: "update_ts_metadata_failed",
    successText: "update_ts_metadata_success",
  },
  [MutationTypes.enum.askIntraday]: {
    title: "ask_intraday",
    enterText: "minute",
    errorText: "ask_intraday_error",
    successText: "ask_intraday_success",
  },
  [MutationTypes.enum.askEnedisStaticData]: {
    title: "ask_enedis_static_data",
    enterText: "minute",
    errorText: "ask_enedis_static_data_failed",
    successText: "ask_enedis_static_data_success",
  },
  [MutationTypes.enum.refreshGSFinance]: {
    title: "refresh_gs_finance",
    enterText: "minute",
    errorText: "refresh_gs_finance_failed",
    successText: "refresh_gs_finance_success",
  },
  //
  [MutationTypes.enum.updateEvent]: {
    title: "update_event",
    enterText: "minute",
    errorText: "update_event_failed",
    successText: "update_event_success",
  },
  [MutationTypes.enum.postEvent]: {
    title: "create_event",
    enterText: "minute",
    errorText: "create_event_failed",
    successText: "create_event_success",
  },
  [MutationTypes.enum.deleteEvent]: {
    title: "delete_event",
    enterText: "minute",
    errorText: "delete_event_failed",
    successText: "delete_event_success",
  },
  [MutationTypes.enum.refreshDatahealth]: {
    title: "refresh_dh",
    enterText: "refresh_dh_enter",
    errorText: "refresh_dh_error",
    successText: "refresh_dh_success",
  },
  [MutationTypes.enum.refreshModelDatahealth]: {
    title: "refresh_model_dh",
    successText: "refresh_model_dh_success",
    errorText: "refresh_model_dh_error",
  },
  // Notes
  [MutationTypes.enum.createNote]: {
    title: "create_note",
    errorText: "create_note_failed",
    successText: "create_note_success",
  },
  [MutationTypes.enum.updateNote]: {
    title: "update_note",
    errorText: "update_note_failed",
    successText: "update_note_success",
  },
  [MutationTypes.enum.deleteNote]: {
    title: "delete_note",
    errorText: "delete_note_failed",
    successText: "delete_note_success",
  },
  [MutationTypes.enum.archiveNote]: {
    title: "archive_note",
    errorText: "archive_failed",
    successText: "archive_success",
  },
  //
  [MutationTypes.enum.deleteDocument]: {
    title: "delete_doc",
    successText: "delete_doc_success",
  },
  [MutationTypes.enum.updateSupplyInvoice]: {
    title: "update_supply_invoice",
    successText: "update_supply_invoice_success",
  },
  [MutationTypes.enum.uploadDocument]: {
    title: "upload_doc",
    successText: "upload_doc_success",
  },
  [MutationTypes.enum.docgenOrder]: {
    title: "ordering",
    successText: "ordering_info",
  },
  [MutationTypes.enum.docgenRefresh]: {
    title: "ordering_refresh",
    successText: "ordering_refresh_info",
  },
  [MutationTypes.enum.cancelInvoice]: {
    title: "ordering_cancelation",
    successText: "ordering_cancelation_canceled",
  },
  [MutationTypes.enum.setInvoiceDefinitiveStatus]: {
    title: "setting",
    successText: "setting_set",
  },
  [MutationTypes.enum.setInvoiceAccounted]: {
    title: "set_invoice_accounted",
    errorText: "set_invoice_accounted_failed",
    successText: "set_invoice_accounted_success",
  },
  [MutationTypes.enum.updateLegalEntity]: {
    title: "update_legal_entity",
    errorText: "update_legal_entity_failed",
    successText: "update_legal_entity_success",
  },
  [MutationTypes.enum.updateLegalEntityAccountingInfos]: {
    title: "update_accounting_infos",
    errorText: "update_accounting_failed",
    successText: "update_accounting_success",
  },
  [MutationTypes.enum.createAddress]: {
    title: "create_address",
    errorText: "create_address_failed",
    successText: "create_address_success",
  },
  [MutationTypes.enum.updateAddress]: {
    title: "update_address",
    errorText: "update_address_failed",
    successText: "update_address_success",
  },
  [MutationTypes.enum.createContact]: {
    title: "create_contact",
    errorText: "create_contact_failed",
    successText: "create_contact_success",
  },
  [MutationTypes.enum.updateContact]: {
    title: "update_contact",
    errorText: "update_contact_failed",
    successText: "update_contact_success",
  },
  [MutationTypes.enum.updateInvoiceLine]: {
    title: "update_invoice_line",
    errorText: "update_invoice_line_failed",
    successText: "update_invoice_line_success",
  },
  [MutationTypes.enum.setInvoiceExtra]: {
    title: "set_invoice_extra",
    errorText: "set_invoice_extra_failed",
    successText: "set_invoice_extra_success",
  },
  [MutationTypes.enum.runPerformanceTest]: {
    title: "run_performance_test",
    enterText: "minute",
    errorText: "run_performance_test_failed",
    successText: "run_performance_test_success",
  },
  [MutationTypes.enum.runSiteHandOver]: {
    title: "run_site_hand_over",
    enterText: "minute",
    errorText: "run_site_hand_over_failed",
    successText: "run_site_hand_over_success",
  },
  [MutationTypes.enum.publish_invoicing_request]: {
    title: "publish_invoicing_request",
    errorText: "publish_invoicing_request_failed",
    successText: "publish_invoicing_request_success",
  },
}

export const MutationAction = z.enum(["update", "delete", "create", "invalidate"])
export type MutationAction = z.infer<typeof MutationAction>

export function getMutationOptions(
  mutationType: MutationTypes,
  options: {
    listKey?: string | string[][] | ((variables: any) => any[] | undefined)
    atomicKey?: string | any[]
    notif?: { atEnter?: NotifLevel }
    refetchType?: "active" | "inactive" | "all" | "none"
    // For mutations whose failure still changes the backend state
    invalidateOnError?: boolean
  } = {},
): {
  onMutate: () => void
  onError: (error: AxiosError, variables: any) => Promise<void>
  onSuccess: (data: any, variables: any, context: any) => void
} {
  const { notif = { atEnter: NotifLevel.enum.info } } = options
  const queryClient = useQueryClient()

  const { t } = useI18n()

  async function invalidateMutated(variables: any): Promise<void> {
    const invalidatePromises = []

    function invalidate(keys: QueryKey): Promise<void> {
      return queryClient.invalidateQueries({
        queryKey: keys,
        refetchType: options.refetchType ?? "all",
      })
    }

    function addToInvalidateAtomic(atomicKey: string, obj: Record<string, any>) {
      if ("uuid" in obj) {
        invalidatePromises.push(invalidate([atomicKey, obj.uuid]))
      } else if ("uuids" in obj && R.isArray(obj.uuids)) {
        const uuids = obj.uuids as string[]
        uuids.forEach((uuid: string) => invalidatePromises.push(invalidate([atomicKey, uuid])))
      }
    }

    // Maybe invalidate atomicKey
    if (options.atomicKey) {
      if (R.isString(options.atomicKey)) {
        if (R.isArray(variables) && variables.every((e: any) => R.isPlainObject(e))) {
          variables.forEach((e: any) => addToInvalidateAtomic(options.atomicKey as string, e))
        } else if (R.isPlainObject(variables)) {
          addToInvalidateAtomic(options.atomicKey, variables)
        }
      } else if (R.isArray(options.atomicKey)) {
        invalidatePromises.push(invalidate(options.atomicKey))
      } else {
        console.error(`Failed to invalidate query with atomicKey being ${options.atomicKey}`)
      }
    }

    // Maybe apply actions on success for listKey:
    if (options.listKey) {
      if (R.isFunction(options.listKey)) {
        const toInvalidate = options.listKey(variables)
        if (toInvalidate && toInvalidate.length) {
          invalidatePromises.push(invalidate(toInvalidate))
        }
      } else if (R.isArray(options.listKey)) {
        options.listKey.forEach((key) => invalidatePromises.push(invalidate(key)))
      } else {
        invalidatePromises.push(invalidate([options.listKey]))
      }
    }

    await Promise.all(invalidatePromises)
  }

  return {
    onMutate: () => {
      if (notif.atEnter && mutationOptions[mutationType].enterText) {
        useNotifStore().addNotification({
          title: t(`components.queries.${mutationOptions[mutationType].title}`),
          text: t(`components.queries.${mutationOptions[mutationType].enterText}`),
          level: notif.atEnter,
        })
      }
    },
    onError: async (error: AxiosError, variables) => {
      // Backend serializes business errors as { name, message, detail } (see
      // zefire api/errors.py ErrorSchema) with an actionable message on 4xx.
      // Genuine 500s only carry a generic "Internal Server Error" phrase, so we
      // ignore their body and show a friendly fallback instead of a blank toast.
      const backendMsg =
        error.response?.status === 500
          ? undefined
          : (error.response?.data as { message?: string } | undefined)?.message
      let text = backendMsg || t("notification.unexpected_error")
      if (mutationOptions[mutationType].errorText) {
        text = t("notification.error_msg", {
          title: t(`components.queries.${mutationOptions[mutationType].errorText}`),
          error_msg: text,
        })
      }
      useNotifStore().addNotification({
        title: t("notification.error", {
          title: t(`components.queries.${mutationOptions[mutationType].title}`),
        }),
        text,
        level: NotifLevel.enum.alert,
      })
      if (options.invalidateOnError) {
        await invalidateMutated(variables)
      }
    },
    onSuccess: async (_, variables) => {
      await invalidateMutated(variables)

      useNotifStore().addNotification({
        title: t("notification.success", {
          title: t(`components.queries.${mutationOptions[mutationType].title}`),
        }),
        text: t(`components.queries.${mutationOptions[mutationType].successText}`),
        level: NotifLevel.enum.success,
      })
    },
  }
}

// map of time before considering to re-do the query
const gcTimesMap = {
  default: 5 * 60 * 1000, // 5 min
  long: 30 * 60 * 1000, // 30 min
  oneDay: 24 * 60 * 60 * 1000, // 24 hours
}

// __________ User Infos __________
//

/**
 * Fetches authenticated user information including meilisearch credentials.
 * User info is cached for 24 hours and refetched automatically when expired.
 * Includes automatic retry on failure.
 */
export function useGetUserInfos() {
  return useQuery({
    queryKey: ["user", "me"],
    queryFn: async () => {
      return await useApiClient().get_me()
    },
    staleTime: gcTimesMap.oneDay,
    cacheTime: gcTimesMap.oneDay,
    retry: 3, // Retry up to 3 times on failure
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff with max 30s
  })
}

// __________ Layout data queries __________
//

export function useFiltersValuesQuery(values: MaybeRef<InferGetInput<"get_filters_values">>) {
  return useQuery({
    queryKey: ["filter", values],
    queryFn: async () => {
      const client = useApiClient()
      const { config } = dispatchEndpointArgs(values, "get_filters_values")
      const data = await client.get_filters_values(config)
      return data
    },
  })
}

export function useDocGenConfigKindsQuery(
  values: MaybeRef<InferGetInput<"get_defined_docgen_configs">>,
) {
  return useQuery({
    queryKey: ["filter", "docgenconfig", values],
    queryFn: async () => {
      const client = useApiClient()
      const { config } = dispatchEndpointArgs(values, "get_defined_docgen_configs")
      const data = await client.get_defined_docgen_configs(config)
      return data
    },
  })
}

// __________ Golden Source - Pastillas __________
//

export function useUnderperformingSitesQuery(
  values: MaybeRef<InferGetInput<"get_underperforming_pastillas">>,
) {
  return useQuery({
    enabled: hasScope(Scope.enum["read:supervision"]),
    queryKey: ["pastillas", "underperforming", values],
    queryFn: async () => {
      const client = useApiClient()
      const data = await client.get_underperforming_pastillas({ params: unref(values) })
      return data
    },
  })
}

export function useGoldenSourceTS(values: MaybeRef<InferGetInput<"get_goldensource_timeseries">>) {
  return useQuery({
    queryKey: ["goldensource", "timeseries", values],
    queryFn: async () => {
      const client = useApiClient()
      const { config } = dispatchEndpointArgs(values, "get_goldensource_timeseries")
      const data = await client.get_goldensource_timeseries(config)
      return data as DataFrame
    },
  })
}

export function useGoldenSourceFinanceTS(
  values: MaybeRef<InferGetInput<"get_goldensource_finance_timeseries">>,
) {
  return useQuery({
    queryKey: ["goldensource", "finance", "timeseries", values],
    queryFn: async () => {
      const client = useApiClient()
      const { config } = dispatchEndpointArgs(values, "get_goldensource_finance_timeseries")
      const data = await client.get_goldensource_finance_timeseries(config)
      return data as GSFinanceTSResponse
    },
  })
}

export function useGoldenSourceGauges(
  values: MaybeRef<InferGetInput<"get_goldensource_gauges">>,
  opts?: { enabled?: MaybeRef<boolean> },
) {
  return useQuery({
    enabled: opts?.enabled,
    queryKey: ["goldensource", "gauges", values],
    queryFn: async () => {
      const client = useApiClient()
      const { config } = dispatchEndpointArgs(values, "get_goldensource_gauges")
      return await client.get_goldensource_gauges(config)
    },
  })
}

export function useGoldenSourceNegPrice(
  values: MaybeRef<InferGetInput<"get_goldensource_negprices">>,
) {
  return useQuery({
    queryKey: ["goldensource", "negprices", values],
    queryFn: async () => {
      const client = useApiClient()
      const { config } = dispatchEndpointArgs(values, "get_goldensource_negprices")
      const data = await client.get_goldensource_negprices(config)
      return data
    },
  })
}

export function useGoldenSourcePastilla(
  values: MaybeRef<InferGetInput<"get_pastillas_filtered_by_model">>,
) {
  return useQuery({
    queryKey: ["goldensource", "pastillas", values],
    queryFn: async () => {
      const client = useApiClient()
      const { config } = dispatchEndpointArgs(values, "get_pastillas_filtered_by_model")
      return await client.get_pastillas_filtered_by_model(config)
    },
  })
}

export function useGoldenSourceGlobalKPIs(
  values: MaybeRef<InferGetInput<"get_goldensource_kpis">>,
) {
  return useQuery({
    queryKey: ["goldensource", "kpis", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_goldensource_kpis")
      const kpis = await useApiClient().get_goldensource_kpis(config)
      return kpis
    },
    cacheTime: gcTimesMap.long,
  })
}

export function useGoldenSourceKPIsPerTechno(
  values: MaybeRef<InferGetInput<"get_goldensource_kpis_per_techno">>,
) {
  return useQuery({
    queryKey: ["goldensource", "kpis", "techno", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_goldensource_kpis_per_techno")
      return await useApiClient().get_goldensource_kpis_per_techno(config)
    },
  })
}

// __________ DataHealth __________
//

export function useDataHealthsQuery(values: MaybeRef<InferGetInput<"get_datahealth">>) {
  return useQuery({
    queryKey: ["datahealth", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_datahealth")
      const dhs = await useApiClient().get_datahealth(config)

      // Do the date coercion manually as we disabled the zod parsing for this endpoint
      for (const dh of dhs) {
        dh.site.commissioning = dh.site.commissioning ? new Date(dh.site.commissioning) : undefined
        dh.last_update = new Date(dh.last_update)
      }
      return dhs
    },
  })
}

export function useSiteDataHealthsQuery(
  values: MaybeRef<InferGetInput<"get_sites_with_datahealth_filtered_by_model">>,
) {
  return useQuery({
    queryKey: ["datahealth", "sites", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_sites_with_datahealth_filtered_by_model")
      const sites = await useApiClient().get_sites_with_datahealth_filtered_by_model(config)

      // Do the date coercion manually as we disabled the zod parsing for this endpoint
      for (const site of sites) {
        site.commissioning = site.commissioning ? new Date(site.commissioning) : undefined
        for (const dh of site.datahealths) {
          dh.last_update = new Date(dh.last_update)
        }
      }
      return sites
    },
  })
}

export function useMutateDataHealthRefreshQuery() {
  return useMutation({
    mutationFn: async (uuid: MaybeRef<string>) => {
      return await useApiClient().datahealth_refresh(undefined, { params: { uuid: unref(uuid) } })
    },
    ...getMutationOptions(MutationTypes.enum.refreshDatahealth, {
      listKey: "datahealth",
      notif: { atEnter: "info" },
    }),
    ...trackMutation("datahealth_refresh_requested", () => ({ scope: "site" })),
  })
}

export function useMutateModelDataHealthRefreshQuery() {
  return useMutation({
    mutationFn: async (values: MaybeRef<InferMutateInput<"datahealth_model_level_refresh">>) => {
      const { body, config } = dispatchEndpointArgs(values, "datahealth_model_level_refresh")
      return await useApiClient().datahealth_model_level_refresh(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.refreshModelDatahealth, {
      listKey: (variables) => (variables.model === "site" ? ["datahealth"] : undefined),
      notif: { atEnter: "info" },
    }),
    ...trackMutation<MaybeRef<InferMutateInput<"datahealth_model_level_refresh">>>(
      "datahealth_refresh_requested",
      (v) => {
        const values = unref(v)
        return {
          scope: values.model,
          check_count: values.checks?.length,
          category_count: values.categories?.length,
        }
      },
    ),
  })
}

// __________ Artifacts __________
//

export function useArtifactsQuery(
  values: MaybeRef<InferGetInput<"get_artifacts_filtered_by_model">>,
) {
  return useQuery({
    queryKey: ["artifacts", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_artifacts_filtered_by_model")
      return await useApiClient().get_artifacts_filtered_by_model(config)
    },
  })
}

export function useArtifactQuery(values: MaybeRef<InferGetInput<"get_artifact">>) {
  return useQuery({
    queryKey: ["artifact", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_artifact")
      return await useApiClient().get_artifact(config)
    },
  })
}

// __________ Timeseries __________
//

export function useTSMetadataQueryFromModel(
  values: MaybeRef<InferGetInput<"get_timeseries_metadata_from_model">>,
) {
  return useQuery({
    queryKey: ["timeseries", values],
    queryFn: async () => {
      const { body, config } = dispatchEndpointArgs(values, "get_timeseries_metadata_from_model")
      return await useApiClient().get_timeseries_metadata_from_model(body, config)
    },
  })
}

export function useGetTsIdsQuery(
  model: MaybeRef<"site" | "contract" | "invoice" | "offtaker" | "assetmanager_legal_entity_link">,
  uuid: MaybeRef<string>,
  tags: MaybeRef<(string | (string | null)[] | null)[]>,
  enabled: MaybeRef<boolean> = true,
) {
  return useQuery({
    queryKey: computed(() => ["tsIds", toValue(model), toValue(uuid), toValue(tags)]),
    queryFn: async () => {
      return await useApiClient().get_timeseries_ids_from_model(
        { tags: toValue(tags) },
        { params: { model: toValue(model), uuid: toValue(uuid) } },
      )
    },
    enabled: computed(() => toValue(enabled)),
  })
}

export function tsDataQueryOptions(
  name: string,
  opts?: { start?: Date | null; end?: Date | null },
) {
  return {
    queryKey: ["tsData", name, opts?.start?.toString(), opts?.end?.toString()],
    queryFn: async () => {
      const params: Record<string, unknown> = { io_format: "json" }
      if (R.isNonNullish(opts?.start)) {
        params.start = dt.formatISO(opts.start)
      }
      if (R.isNonNullish(opts?.end)) {
        params.end = dt.formatISO(opts.end)
      }
      const r = await useAxiosInstance().get(`/api/timeseries/${name}/data`, { params })
      return r.data as { index: number[]; data: number[]; name: string }
    },
  }
}

export function useTimeseriesDataQuery(
  tablename: MaybeRef<string | undefined>,
  opts?: { start?: MaybeRef<Date | null | undefined>; end?: MaybeRef<Date | null | undefined> },
) {
  const resolvedOpts = computed(() =>
    tsDataQueryOptions(toValue(tablename) ?? "", {
      start: toValue(opts?.start),
      end: toValue(opts?.end),
    }),
  )
  return useQuery({
    queryKey: computed(() => resolvedOpts.value.queryKey),
    queryFn: () => resolvedOpts.value.queryFn(),
    enabled: computed(() => R.isNonNullish(toValue(tablename))),
  })
}

export function useTSMetadataQuery(tablename: MaybeRef<string | undefined>) {
  return useQuery({
    queryKey: computed(() => ["tsMetadata", toValue(tablename)]),
    queryFn: async () => {
      return await useApiClient().get_timeseries_metadata({
        params: { name: toValue(tablename)! },
      })
    },
    enabled: computed(() => R.isNonNullish(toValue(tablename))),
  })
}

export function useTSDataMutateQuery() {
  return useMutation({
    mutationFn: async (values: MaybeRef<InferMutateInput<"post_timeseries_data">>) => {
      const { body, config } = dispatchEndpointArgs(values, "post_timeseries_data")
      await useApiClient().post_timeseries_data(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updateTSData, {
      listKey: [["artifacts"], ["artifact"], ["tsData"]],
    }),
    ...trackMutation<MaybeRef<InferMutateInput<"post_timeseries_data">>>(
      "timeseries_edited",
      (v) => ({ kind: "data", record_count: unref(v).records.length }),
    ),
  })
}

export function useTSMetadataMutateQuery() {
  return useMutation({
    mutationFn: async (values: MaybeRef<InferMutateInput<"patch_timeseries_metadata">>) => {
      const { body, config } = dispatchEndpointArgs(values, "patch_timeseries_metadata")
      return await useApiClient().patch_timeseries_metadata(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updateTSMetadata, {
      listKey: [["tsMetadata"], ["artifacts"], ["siteMetering"]],
    }),
    ...trackMutation("timeseries_edited", () => ({ kind: "metadata" })),
  })
}

// __________ Documents (new generation files queries) __________
//

export function useDocumentsQuery(
  values: MaybeRef<InferGetInput<"get_documents_filtered_by_model">>,
) {
  return useQuery({
    enabled: hasScope(Scope.enum["read:document"]),
    queryKey: ["documents", unref(values).uuid],
    queryFn: async () => {
      const client = useApiClient()
      const { config } = dispatchEndpointArgs(values, "get_documents_filtered_by_model")
      const data = await client.get_documents_filtered_by_model(config)
      return z.array(DocumentSchemaShort).parse(data)
    },
  })
}

export function useDocumentsDeleteMutateQuery() {
  return useMutation({
    mutationFn: async (data: { uuids: DocumentSchemaShort["uuid"][] }) => {
      const client = useApiClient()
      const promises = R.map(data.uuids, (uuid) =>
        client.delete_document(undefined, { params: { uuid } }),
      )
      await Promise.all(promises)
    },
    ...getMutationOptions(MutationTypes.enum.deleteDocument, {
      listKey: "documents",
      atomicKey: "document",
    }),
    ...trackMutation<{ uuids: string[] }>("document_deleted", (v) => ({
      document_count: v.uuids.length,
    })),
  })
}

export function useOrderDocGenMutateQuery() {
  return useMutation({
    mutationFn: async (values: {
      model: ProducerCompanyKind | "site"
      uuids: string[]
      wait?: boolean
      [key: string]: any
    }) => {
      const client = useApiClient()
      const { model, wait, ...body } = values

      // If wait=true, generate synchronously and download
      if (wait) {
        // Generate documents with wait=true
        const documents = await client.generate_documents(body as any, {
          params: { model },
          queries: { wait: true },
        })
        // Download the generated documents
        const axios = useAxiosInstance()
        const responses = await Promise.all(
          documents.map((doc) =>
            axios.get(`api/document/${doc.uuid}/content`, { responseType: "blob" }),
          ),
        )
        await dispatchFileResponses(responses)
      } else {
        // Default behavior: async generation without wait
        await client.generate_documents(body as any, { params: { model } })
      }
    },
    ...getMutationOptions(MutationTypes.enum.docgenOrder, {
      listKey: undefined,
      atomicKey: undefined,
    }),
    ...trackMutation<{ model: string; uuids: string[]; wait?: boolean; kind?: string }>(
      "document_generated",
      (v) => ({
        action: "order",
        report_kind: v.kind,
        target_model: v.model,
        target_count: v.uuids.length,
        synchronous: v.wait ?? false,
      }),
    ),
  })
}

export function useDocumentUploadMutateQuery() {
  return useMutation({
    mutationFn: async (values: MaybeRef<InferMutateInput<"create_document">>) => {
      const { body, config } = dispatchEndpointArgs(values, "create_document")
      return await useApiClient().create_document(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.uploadDocument, {
      listKey: "documents",
    }),
    ...trackMutation<MaybeRef<InferMutateInput<"create_document">>>("document_uploaded", (v) => {
      const values = unref(v)
      return {
        document_kind: values.kind,
        target_model: values.model,
        is_attachment: values.is_attachment,
        file_count: values.files.length,
      }
    }),
  })
}

export function useDocumentRefreshMutateQuery() {
  return useMutation({
    mutationFn: async (data: { uuids: string[] }) => {
      const client = useApiClient()
      await client.refresh_documents({ uuids: data.uuids })
    },
    ...getMutationOptions(MutationTypes.enum.docgenRefresh, {
      listKey: "documents",
      atomicKey: "document",
    }),
    ...trackMutation<{ uuids: string[] }>("document_generated", (v) => ({
      action: "refresh",
      document_count: v.uuids.length,
    })),
  })
}

// __________ Download Mutations __________
//

export function useDownloadDocumentAndOpenMutation() {
  const { t } = useI18n()
  return useMutation({
    mutationFn: async ({ uuid, filename }: { uuid: string; filename: string }) => {
      const axios = useAxiosInstance()
      const resp = await axios.get(`api/document/${uuid}/content`, { responseType: "blob" })
      const blob = new Blob([resp.data], { type: inlineViewMimeType(filename) })
      const url = URL.createObjectURL(blob)
      const win = window.open(url, "_blank")
      win?.focus()
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    },
    retry: defaultRetry,
    onMutate: () => NProgress.start(),
    onSettled: () => NProgress.done(),
    onError: () => {
      useNotifStore().addNotification({
        title: t("components.queries.download_document"),
        level: NotifLevel.enum.alert,
        text: t("components.queries.download_document_failed"),
      })
    },
    ...trackMutation("document_downloaded", () => ({ mode: "preview", document_count: 1 })),
  })
}

export function useDownloadDocumentsMutation() {
  const { t } = useI18n()
  return useMutation({
    mutationFn: async (uuids: string[]) => {
      const axios = useAxiosInstance()
      if (uuids.length === 1) {
        const resp = await axios.get(`api/document/${uuids[0]}/content`, { responseType: "blob" })
        await dispatchFileResponses([resp])
      } else {
        const resp = await axios.post(
          "api/document/content",
          { uuid: uuids },
          { responseType: "blob" },
        )
        await dispatchFileResponses([resp])
      }
    },
    retry: defaultRetry,
    onMutate: () => NProgress.start(),
    onSettled: () => NProgress.done(),
    onError: () => {
      useNotifStore().addNotification({
        title: t("components.queries.download_documents"),
        level: NotifLevel.enum.alert,
        text: t("components.queries.download_documents_failed"),
      })
    },
    ...trackMutation<string[]>("document_downloaded", (v) => ({
      mode: "download",
      document_count: v.length,
    })),
  })
}

export function useDocumentDownloadPDF(
  uuid: MaybeRef<string | undefined>,
  enabled: MaybeRef<boolean>,
  i18n: { t: (value: string) => string },
) {
  return useQuery({
    enabled: () =>
      hasScope(Scope.enum["read:document"]) && toValue(enabled) && R.isNonNullish(toValue(uuid)),
    queryKey: ["document", uuid, "content"],
    queryFn: async () => {
      try {
        const axios = useAxiosInstance()
        const resp = await axios.get(`api/document/${toValue(uuid)}/content`, {
          responseType: "blob",
        })
        // The document content endpoint returns a generic content-type, so the
        // raw blob is octet-stream and an iframe downloads it instead of rendering.
        // Re-wrap with the pdf mime type (as useDownloadDocumentAndOpenMutation does).
        return new Blob([resp.data], { type: "application/pdf" })
      } catch (err) {
        useNotifStore().addNotification({
          title: i18n.t("notification.missing_pdf.title"),
          level: NotifLevel.enum.alert,
          text: i18n.t("notification.missing_pdf.message"),
        })
        throw err
      }
    },
  })
}

export function useDownloadInvoicesPDFMutation() {
  const { t } = useI18n()
  return useMutation({
    mutationFn: async (uuids: InvoiceSchemaShort["uuid"][]) => {
      const axios = useAxiosInstance()
      if (uuids.length === 1) {
        const resp = await axios.get(`api/invoices/${uuids[0]}/document`, {
          responseType: "blob",
        })
        await dispatchFileResponses([resp])
      } else {
        const resp = await axios.post(
          "api/invoices/document",
          { uuid: uuids },
          { responseType: "blob" },
        )
        await dispatchFileResponses([resp])
      }
    },
    retry: defaultRetry,
    onMutate: () => NProgress.start(),
    onSettled: () => NProgress.done(),
    onError: () => {
      useNotifStore().addNotification({
        title: t("components.queries.download_invoice_pdf"),
        level: NotifLevel.enum.alert,
        text: t("components.queries.download_invoice_pdf_failed"),
      })
    },
    ...trackMutation<string[]>("invoice_exported", (v) => ({
      format: "pdf",
      invoice_count: v.length,
    })),
  })
}

export function useDownloadInvoicesExcelMutation() {
  const { t } = useI18n()
  return useMutation({
    mutationFn: async (uuids: InvoiceSchemaShort["uuid"][]) => {
      const axios = useAxiosInstance()
      if (uuids.length === 1) {
        const resp = await axios.get(`api/invoices/${uuids[0]}/excel`, { responseType: "blob" })
        await dispatchFileResponses([resp])
      } else {
        const resp = await axios.post(
          "api/invoices/excel",
          { uuid: uuids },
          { responseType: "blob" },
        )
        await dispatchFileResponses([resp])
      }
    },
    retry: defaultRetry,
    onMutate: () => NProgress.start(),
    onSettled: () => NProgress.done(),
    onError: () => {
      useNotifStore().addNotification({
        title: t("components.queries.download_invoice_excel"),
        level: NotifLevel.enum.alert,
        text: t("components.queries.download_invoice_excel_failed"),
      })
    },
    ...trackMutation<string[]>("invoice_exported", (v) => ({
      format: "excel",
      invoice_count: v.length,
    })),
  })
}

// __________ Site __________
//
// A reactive uuid switches site; a nullish one disables the query.
export function useSiteQuery(site_uuid: MaybeRef<string | null | undefined>) {
  return useQuery({
    queryKey: ["site", site_uuid, "topo"],
    queryFn: async () => await useApiClient().get_site({ params: { uuid: unref(site_uuid)! } }),
    enabled: computed(() => R.isNonNullish(unref(site_uuid))),
  })
}

export function useSitesSummaryQuery(company_uuid: MaybeRef<string | undefined>) {
  return useQuery({
    queryKey: ["siteSummary", company_uuid],
    queryFn: async () =>
      useApiClient().portfolio_site_summary({ queries: { company_uuid: unref(company_uuid) } }),
  })
}

export function useCompanySitesQuery(
  values: MaybeRef<InferGetInput<"get_sites_filtered_by_model">>,
) {
  return useQuery({
    queryKey: ["sites", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_sites_filtered_by_model")
      return await useApiClient().get_sites_filtered_by_model(config)
    },
    cacheTime: gcTimesMap.long,
  })
}

export function useSiteMutateQuery() {
  return useMutation({
    mutationFn: async (values: MaybeRef<InferMutateInput<"update_site">>) => {
      const { body, config } = dispatchEndpointArgs(values, "update_site")
      await useApiClient().update_site(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updatePortfolio, {
      listKey: "sites",
      atomicKey: "site",
    }),
    ...trackMutation("site_updated"),
  })
}

export function useSiteProductionQuery(
  values: MaybeRef<InferGetInput<"get_site_production_chart">>,
  opts?: { enabled?: MaybeRef<boolean> },
) {
  return useQuery({
    enabled: opts?.enabled,
    queryKey: ["site", "power_production", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_site_production_chart")
      const data = await useApiClient().get_site_production_chart(config)
      return data as DataFrame
    },
  })
}

export function useSiteReactiveChartQuery(
  values: MaybeRef<InferGetInput<"get_site_reactive_chart">>,
) {
  return useQuery({
    queryKey: ["site", "reactive", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_site_reactive_chart")
      const data = await useApiClient().get_site_reactive_chart(config)
      return data
    },
  })
}

export function useSiteVoltageChartQuery(
  values: MaybeRef<InferGetInput<"get_site_voltage_chart">>,
) {
  return useQuery({
    queryKey: ["site", "voltage", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_site_voltage_chart")
      const data = await useApiClient().get_site_voltage_chart(config)
      return data as DataFrame
    },
  })
}

export function useDeliveryPointsMutateQuery() {
  return useMutation({
    mutationFn: async (data: {
      uuid: SiteSchema["uuid"]
      values: InferMutateInput<"update_deliverypoint">
    }) => {
      const { body, config } = dispatchEndpointArgs(data.values, "update_deliverypoint")
      await useApiClient().update_deliverypoint(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updateDeliveryPoints, {
      listKey: "sites",
      atomicKey: "site",
    }),
    ...trackMutation("delivery_point_updated"),
  })
}

export function useCreateDPContractualMappingMutateQuery() {
  return useMutation({
    mutationFn: async (data: {
      uuid: SiteSchema["uuid"]
      values: InferMutateInput<"create_dp_contractual_mapping">
    }) => {
      const { body, config } = dispatchEndpointArgs(data.values, "create_dp_contractual_mapping")
      await useApiClient().create_dp_contractual_mapping(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.createDPContractualMapping, {
      listKey: "sites",
      atomicKey: "site",
    }),
    ...trackMutation("dp_contractual_mapping_changed", () => ({ action: "create" })),
  })
}

export function useUpdateDPContractualMappingMutateQuery() {
  return useMutation({
    mutationFn: async (data: {
      uuid: SiteSchema["uuid"]
      values: InferMutateInput<"update_dp_contractual_mapping">
    }) => {
      const { body, config } = dispatchEndpointArgs(data.values, "update_dp_contractual_mapping")
      await useApiClient().update_dp_contractual_mapping(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updateDPContractualMapping, {
      listKey: "sites",
      atomicKey: "site",
    }),
    ...trackMutation("dp_contractual_mapping_changed", () => ({ action: "update" })),
  })
}

export function useDeleteDPContractualMappingMutateQuery() {
  return useMutation({
    mutationFn: async (data: { uuid: SiteSchema["uuid"]; mapping_uuid: string }) =>
      await useApiClient().delete_dp_contractual_mapping(undefined, {
        params: { uuid: data.mapping_uuid },
      }),
    ...getMutationOptions(MutationTypes.enum.deleteDPContractualMapping, {
      listKey: "sites",
      atomicKey: "site",
    }),
    ...trackMutation("dp_contractual_mapping_changed", () => ({ action: "delete" })),
  })
}

export function useSiteMeteringQuery(site_uuid: string) {
  return useQuery({
    queryKey: ["siteMetering", site_uuid],
    queryFn: async () => useApiClient().get_site_metering({ params: { uuid: site_uuid } }),
  })
}

export function useSiteEnedisBackfill() {
  return useMutation({
    mutationFn: async (values: MaybeRef<InferMutateInput<"trigger_enedis_backfill">>) => {
      const { body, config } = dispatchEndpointArgs(values, "trigger_enedis_backfill")
      await useApiClient().trigger_enedis_backfill(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.askBackfill, {
      listKey: [["artifacts"], ["artifact"]],
    }),
    ...trackMutation<MaybeRef<InferMutateInput<"trigger_enedis_backfill">>>(
      "backfill_requested",
      (v) => {
        const values = unref(v)
        return {
          source: "enedis",
          way_count: values.ways?.length,
          measure_type_count: values.measure_types?.length,
        }
      },
    ),
  })
}

export function useSiteSPFactoryBackfill() {
  return useMutation({
    mutationFn: async (values: MaybeRef<InferMutateInput<"trigger_sp_factory_backfill">>) => {
      const { body, config } = dispatchEndpointArgs(values, "trigger_sp_factory_backfill")
      await useApiClient().trigger_sp_factory_backfill(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.askBackfill, {
      listKey: [["artifacts"], ["artifact"]],
    }),
    ...trackMutation<MaybeRef<InferMutateInput<"trigger_sp_factory_backfill">>>(
      "backfill_requested",
      (v) => ({ source: "spfactory", order_count: unref(v).orders.length }),
    ),
  })
}

export function useSiteEnedisIntradayOrder() {
  return useMutation({
    mutationFn: async (uuid: string) => {
      await useApiClient().trigger_enedis_intraday_order(undefined, {
        params: { uuid: uuid },
      })
    },
    ...getMutationOptions(MutationTypes.enum.askIntraday, {
      listKey: [["artifacts"], ["artifact"]],
    }),
    ...trackMutation("backfill_requested", () => ({ source: "enedis_intraday" })),
  })
}

export function useSiteEnedisStaticDataOrder() {
  return useMutation({
    mutationFn: async (uuid: string) => {
      await useApiClient().trigger_enedis_static_data_order(undefined, {
        params: { uuid },
      })
    },
    ...getMutationOptions(MutationTypes.enum.askEnedisStaticData),
    ...trackMutation("backfill_requested", () => ({ source: "enedis_static_data" })),
  })
}

export function useRefreshGSFinance() {
  return useMutation({
    mutationFn: async (values: MaybeRef<InferMutateInput<"refresh_goldensources_finance">>) => {
      const { body, config } = dispatchEndpointArgs(values, "refresh_goldensources_finance")
      await useApiClient().refresh_goldensources_finance(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.refreshGSFinance, {
      listKey: [["goldensource", "finance"]],
    }),
    ...trackMutation<MaybeRef<InferMutateInput<"refresh_goldensources_finance">>>(
      "goldensource_finance_refresh_requested",
      (v) => {
        const values = unref(v)
        const targetType = values.site_uuids?.length
          ? "site"
          : values.contract_uuids?.length
            ? "contract"
            : "all"
        return { target_type: targetType }
      },
    ),
  })
}

// Request input type: `to_refresh` / `refresh_freqs` are optional and defaulted by the schema.
type GSRefreshInput = z.input<typeof GSRefreshParams>

export function useRefreshGoldensourcesMutateQuery() {
  return useMutation({
    // One request per target model: the endpoint refreshes a batch of uuids of a single model.
    mutationFn: async (values: GSRefreshInput[]) => {
      const client = useApiClient()
      await Promise.all(values.map((params) => client.refresh_goldensources(params)))
    },
    ...trackMutation<GSRefreshInput[]>("goldensource_refresh_requested", (values) => ({
      target_count: R.sumBy(values, (params) => params.uuids.length),
      category_count: values[0]?.to_refresh?.length,
      frequency_count: values[0]?.refresh_freqs?.length,
    })),
  })
}

// __________ Site Status History __________
//

export function useCreateSiteStatusMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"log_site_status">) => {
      const { body, config } = dispatchEndpointArgs(values, "log_site_status")
      return await useApiClient().log_site_status(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.createSiteStatus, {
      listKey: [["site"], ["commissioning"]],
    }),
    ...trackMutation<InferMutateInput<"log_site_status">>("site_status_changed", (v) => ({
      action: "create",
      status: v.status,
    })),
  })
}
export function useUpdateSiteStatusMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"edit_site_status">) => {
      const { body, config } = dispatchEndpointArgs(values, "edit_site_status")
      return await useApiClient().edit_site_status(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updateSiteStatus, {
      listKey: [["site"], ["commissioning"]],
    }),
    ...trackMutation<InferMutateInput<"edit_site_status">>("site_status_changed", (v) => ({
      action: "update",
      status: v.status,
    })),
  })
}
export function useDeleteSiteStatusMutateQuery() {
  return useMutation({
    mutationFn: async (uuid: string) =>
      await useApiClient().delete_site_status(undefined, { params: { uuid } }),
    ...getMutationOptions(MutationTypes.enum.deleteSiteStatus, {
      listKey: [["site"], ["commissioning"]],
    }),
    ...trackMutation("site_status_changed", () => ({ action: "delete" })),
  })
}

export function useRunPerformanceTestMutateQuery() {
  return useMutation({
    mutationFn: async (args: { uuid: string; permissive?: boolean; failure_mode?: boolean }) =>
      await useApiClient().run_performance_test(undefined, {
        params: { uuid: args.uuid },
        queries: { permissive: args.permissive, failure_mode: args.failure_mode },
      }),
    ...getMutationOptions(MutationTypes.enum.runPerformanceTest, {
      listKey: [["site"], ["documents"], ["commissioning"]],
    }),
    ...trackMutation<{ permissive?: boolean; failure_mode?: boolean }>(
      "commissioning_run",
      (v) => ({
        kind: "performance_test",
        permissive: v.permissive ?? false,
        failure_mode: v.failure_mode ?? false,
      }),
    ),
  })
}

export function useRunSiteHandOverMutateQuery() {
  return useMutation({
    mutationFn: async (uuid: string) =>
      await useApiClient().run_site_hand_over(undefined, {
        params: { uuid },
      }),
    ...getMutationOptions(MutationTypes.enum.runSiteHandOver, {
      listKey: [["site"], ["documents"], ["commissioning"]],
    }),
    ...trackMutation("commissioning_run", () => ({ kind: "hand_over" })),
  })
}

// __________ Legal entities __________
//
export function useLegalEntityInfoQuery(legalEntityUuid: string) {
  return useQuery({
    queryKey: ["legal-entity", legalEntityUuid],
    queryFn: async () => {
      return await useApiClient().get_legal_entity_details({
        params: { uuid: legalEntityUuid },
      })
    },
  })
}

export function useCounterpartiesQuery() {
  return useQuery({
    enabled: hasScope(Scope.enum["read:partner"]),
    queryKey: ["counterparties"],
    queryFn: async () => await useApiClient().get_counterparties(),
  })
}

export function useLegalEntitiesQuery() {
  return useQuery({
    queryKey: ["legal-entities"],
    queryFn: async () => {
      return await useApiClient().get_legal_entities()
    },
  })
}

export async function patchLegalEntity(values: InferMutateInput<"update_legal_entity">) {
  const { uuid, ...body } = values

  // The generated request schema is a union of two partial objects. Zod selects the
  // person branch first and strips company-only fields such as contact associations.
  const response = await useAxiosInstance().patch(`/api/legal_entities/${uuid}`, body)
  return response.data
}

export function useLegalEntityMutateQuery() {
  return useMutation({
    mutationFn: patchLegalEntity,
    ...getMutationOptions(MutationTypes.enum.updateLegalEntity, {
      listKey: [["legal-entities"], ["address"]],
      atomicKey: "legal-entity",
    }),
    ...trackMutation("legal_entity_updated", () => ({ kind: "identity" })),
  })
}

export function useLegalEntityAccountingInfosMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"update_legal_entity_accounting_infos">) => {
      const { body, config } = dispatchEndpointArgs(values, "update_legal_entity_accounting_infos")
      return await useApiClient().update_legal_entity_accounting_infos(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updateLegalEntityAccountingInfos, {
      listKey: [["legal-entities"], ["legal-entity"]],
      atomicKey: "legal-entity",
    }),
    ...trackMutation("legal_entity_updated", () => ({ kind: "accounting" })),
  })
}

// __________ Informations __________
export function useAddressQuery(addressUuid: string) {
  return useQuery({
    queryKey: ["address", addressUuid],
    queryFn: async () => {
      return await useApiClient().get_address_details({ params: { uuid: addressUuid } })
    },
  })
}

export function useAddressListingQuery() {
  return useQuery({
    queryKey: ["addresses"],
    queryFn: async () => {
      return await useApiClient().get_addresses()
    },
  })
}

export function useUpdateAddressMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"update_address">) => {
      const { body, config } = dispatchEndpointArgs(values, "update_address")
      return await useApiClient().update_address(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updateAddress, {
      listKey: [["addresses"], ["legal-entities"], ["legal-entity"]],
      atomicKey: "address",
    }),
    ...trackMutation("address_changed", () => ({ action: "update" })),
  })
}

export function useCreateAddressMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"create_address">) => {
      const { body, config } = dispatchEndpointArgs(values, "create_address")
      return await useApiClient().create_address(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.createAddress, {
      listKey: "addresses",
      atomicKey: "address",
    }),
    ...trackMutation("address_changed", () => ({ action: "create" })),
  })
}

// __________ Contact __________

export function useContactQuery(contactUuid: string) {
  return useQuery({
    queryKey: ["contact", contactUuid],
    queryFn: async () => {
      return await useApiClient().get_contact_info_details({ params: { uuid: contactUuid } })
    },
  })
}

export function useContactListingQuery() {
  return useQuery({
    queryKey: ["contacts"],
    queryFn: async () => {
      return await useApiClient().get_contact_infos()
    },
  })
}

export function useUpdateContactMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"update_contact_info">) => {
      const { body, config } = dispatchEndpointArgs(values, "update_contact_info")
      return await useApiClient().update_contact_info(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updateContact, {
      listKey: [["contacts"], ["legal-entities"], ["legal-entity"]],
      atomicKey: "contact",
    }),
    ...trackMutation("contact_updated"),
  })
}

export function useCreateContactMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"create_contact_info">) => {
      const { body, config } = dispatchEndpointArgs(values, "create_contact_info")
      return await useApiClient().create_contact_info(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.createContact, {
      listKey: "contacts",
      atomicKey: "contact",
    }),
    ...trackMutation("contact_created"),
  })
}
//
// __________ Events __________
//
export function useEventsQuery(
  values: MaybeRef<InferGetInput<"get_events_filtered_by_model">>,
  opts?: { enabled?: MaybeRef<boolean> },
) {
  // Serialize dates to ISO strings for stable query key comparison
  // (Date objects compare by reference, not value)
  const queryKey = computed(() => {
    const v = unref(values)
    return [
      "events",
      v.model,
      v.uuid,
      v.start instanceof Date ? v.start.toISOString() : v.start,
      v.end instanceof Date ? v.end.toISOString() : v.end,
      v.portfolio_names,
      v.is_site_external,
      v.is_event_external,
      v.technos,
      v.assetowner_names,
      v.buyer_names,
      v.network_voltage_codes,
      v.is_validated,
      v.sources,
      v.causes,
    ]
  })

  return useQuery({
    enabled: () => hasScope(Scope.enum["read:event"]) && toValue(opts?.enabled ?? true),
    queryKey,
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_events_filtered_by_model")
      return await useApiClient().get_events_filtered_by_model(config)
    },
  })
}

export function useEventMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"update_event">) => {
      const { body, config } = dispatchEndpointArgs(values, "update_event")
      return await useApiClient().update_event(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updateEvent, {
      listKey: "events",
      atomicKey: "event",
    }),
    ...trackMutation<InferMutateInput<"update_event">>("operational_event_updated", (v) => ({
      event_type: v.event_type,
      is_validated: v.is_validated,
    })),
  })
}

export function usePostEventMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"create_event">) => {
      const { body, config } = dispatchEndpointArgs(values, "create_event")
      return await useApiClient().create_event(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.postEvent, {
      listKey: "events",
    }),
    ...trackMutation<InferMutateInput<"create_event">>("operational_event_created", (v) => {
      const hasUnavailable = R.isNonNullish(v.unavailable_capacity ?? v.unavailable_percentage)
      const hasAvailable = R.isNonNullish(v.available_capacity ?? v.available_percentage)
      return {
        event_type: v.event_type,
        is_external: v.is_external,
        is_validated: v.is_validated,
        capacity_type: hasUnavailable ? "unavailable" : hasAvailable ? "available" : undefined,
      }
    }),
  })
}

export function useDeleteEventMutateQuery() {
  return useMutation({
    mutationFn: async (uuid: string) => await useApiClient().delete_events({ uuids: [uuid] }),
    ...getMutationOptions(MutationTypes.enum.deleteEvent, {
      listKey: "events",
    }),
    ...trackMutation("operational_event_deleted", () => ({ event_count: 1 })),
  })
}

export function useBulkDeleteEventMutateQuery() {
  return useMutation({
    mutationFn: async (values: { uuids: string[] }) =>
      await useApiClient().delete_events({ uuids: values.uuids }),
    ...getMutationOptions(MutationTypes.enum.deleteEvent, {
      listKey: "events",
    }),
    ...trackMutation<{ uuids: string[] }>("operational_event_deleted", (v) => ({
      event_count: v.uuids.length,
    })),
  })
}

// __________ Notes __________
//
export function useNotesQuery(values: MaybeRef<InferGetInput<"get_notes_filtered_by_model">>) {
  return useQuery({
    enabled: hasScope(Scope.enum["read:note"]),
    queryKey: ["notes", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_notes_filtered_by_model")
      return await useApiClient().get_notes_filtered_by_model(config)
    },
  })
}

export function useUpdateNoteMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"patch_note">) => {
      const { body, config } = dispatchEndpointArgs(values, "patch_note")
      return await useApiClient().patch_note(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updateNote, {
      listKey: "notes",
    }),
    ...trackMutation<InferMutateInput<"patch_note">>("note_updated", (v) => ({
      tag_count: v.tags?.length,
      pinned: v.pinned,
    })),
  })
}

export function useCreateNoteMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"create_note">) => {
      const { body, config } = dispatchEndpointArgs(values, "create_note")
      return await useApiClient().create_note(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.createNote, {
      listKey: "notes",
    }),
    ...trackMutation<InferMutateInput<"create_note">>("note_created", (v) => ({
      target_model: v.model,
      tag_count: v.tags?.length ?? 0,
      pinned: v.pinned ?? false,
    })),
  })
}

export function useDeleteNoteMutateQuery() {
  return useMutation({
    mutationFn: async (uuid: string) =>
      await useApiClient().delete_note(undefined, { params: { uuid } }),
    ...getMutationOptions(MutationTypes.enum.deleteNote, {
      listKey: "notes",
    }),
    ...trackMutation("note_removed", () => ({ action: "delete", note_count: 1 })),
  })
}

export function useArchiveNoteMutateQuery() {
  return useMutation({
    mutationFn: async (uuid: string) =>
      await useApiClient().archive_note(undefined, { params: { uuid } }),
    ...getMutationOptions(MutationTypes.enum.archiveNote, {
      listKey: "notes",
    }),
    ...trackMutation("note_removed", () => ({ action: "archive", note_count: 1 })),
  })
}

export function useBulkDeleteNoteMutateQuery() {
  return useMutation({
    mutationFn: async (values: { uuids: string[] }) =>
      await Promise.all(
        values.uuids.map((uuid) => useApiClient().delete_note(undefined, { params: { uuid } })),
      ),
    ...getMutationOptions(MutationTypes.enum.deleteNote, {
      listKey: "notes",
    }),
    ...trackMutation<{ uuids: string[] }>("note_removed", (v) => ({
      action: "delete",
      note_count: v.uuids.length,
    })),
  })
}

export function useBulkArchiveNoteMutateQuery() {
  return useMutation({
    mutationFn: async (values: { uuids: string[] }) =>
      await Promise.all(
        values.uuids.map((uuid) => useApiClient().archive_note(undefined, { params: { uuid } })),
      ),
    ...getMutationOptions(MutationTypes.enum.archiveNote, {
      listKey: "notes",
    }),
    ...trackMutation<{ uuids: string[] }>("note_removed", (v) => ({
      action: "archive",
      note_count: v.uuids.length,
    })),
  })
}

// __________ NoteTags __________

export function useNoteTagQuery(values: MaybeRef<InferGetInput<"get_note_tag">>) {
  return useQuery({
    enabled: hasScope(Scope.enum["read:note"]),
    queryKey: ["notetags", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_note_tag")
      return await useApiClient().get_note_tag(config)
    },
  })
}

export function useUpdateNoteTagMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"patch_notetag">) => {
      const { body, config } = dispatchEndpointArgs(values, "patch_notetag")
      return await useApiClient().patch_notetag(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updateNote, {
      listKey: "notetags",
      atomicKey: "notetag",
    }),
    ...trackMutation("note_tag_changed", () => ({ action: "update" })),
  })
}

export function useCreateNoteTagMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"create_notetag">) => {
      const { body, config } = dispatchEndpointArgs(values, "create_notetag")
      return await useApiClient().create_notetag(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.createNote, {
      listKey: "notetags",
    }),
    ...trackMutation("note_tag_changed", () => ({ action: "create" })),
  })
}

export function useDeleteNoteTagMutateQuery() {
  return useMutation({
    mutationFn: async (uuid: string) =>
      await useApiClient().delete_notetag(undefined, { params: { uuid } }),
    ...getMutationOptions(MutationTypes.enum.deleteNote, {
      listKey: "notetags",
    }),
    ...trackMutation("note_tag_changed", () => ({ action: "delete" })),
  })
}

// __________ Contract __________
//
export function useContractQuery(contract_uuid: string) {
  return useQuery({
    enabled: hasScope(Scope.enum["read:contract"]),
    queryKey: ["contract", contract_uuid],
    queryFn: async () => await useApiClient().get_contract({ params: { uuid: contract_uuid } }),
  })
}

// Reveals the decrypted platform credential (e.g. EDF OA account password) on demand.
// The query only fires once `enabled` is flipped (the user asks for the password) and the
// user holds the `write:invoice` scope. The decrypted secret is never cached: it is
// garbage-collected as soon as the modal stops observing it.
export function useContractPlatformCredentialQuery(
  contract_uuid: MaybeRef<string>,
  enabled: MaybeRef<boolean>,
) {
  return useQuery({
    enabled: () => hasScope(Scope.enum["write:invoice"]) && toValue(enabled),
    queryKey: ["contract", contract_uuid, "platform-credential"],
    queryFn: async () =>
      await useApiClient().get_contract_platform_credential({
        params: { uuid: toValue(contract_uuid) },
      }),
    cacheTime: 0,
    staleTime: 0,
    retry: false,
  })
}

export function useContractsQuery(
  values: MaybeRef<InferGetInput<"get_contracts_filtered_by_model">>,
) {
  return useQuery({
    enabled: hasScope(Scope.enum["read:contract"]),
    queryKey: ["contracts", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_contracts_filtered_by_model")
      return await useApiClient().get_contracts_filtered_by_model(config)
    },
    cacheTime: gcTimesMap.long,
  })
}

export function useContractsSummaryQuery(
  values: MaybeRef<InferGetInput<"portfolio_contract_summary">>,
) {
  return useQuery({
    enabled: hasScope(Scope.enum["read:contract"]),
    queryKey: ["contract_summary", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "portfolio_contract_summary")
      return await useApiClient().portfolio_contract_summary(config)
    },
  })
}

export function useContractMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"update_contract">) => {
      const { body, config } = dispatchEndpointArgs(values, "update_contract")
      return await useApiClient().update_contract(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updateContract, {
      listKey: "contracts",
      atomicKey: "contract",
    }),
    ...trackMutation<InferMutateInput<"update_contract">>("contract_updated", (v) => ({
      invoice_source: v.invoice_source,
      invoice_submit_channel: v.invoice_submit_channel,
      invoicing_granularity: v.invoicing_granularity,
    })),
  })
}

export function useClauseMutateQuery() {
  return useMutation({
    mutationFn: async (values: {
      uuid: ContractSchema["uuid"]
      clause_data: InferMutateInput<"update_clause">
    }) => {
      const { body, config } = dispatchEndpointArgs(values.clause_data, "update_clause")
      return await useApiClient().update_clause(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updateClause, {
      atomicKey: "contract",
    }),
    ...trackMutation<{ clause_data: InferMutateInput<"update_clause"> }>("clause_updated", (v) => ({
      clause_type: v.clause_data.parameters?.type,
    })),
  })
}

// __________ Supervision __________
//
export function useSiteSupervisionQuery(
  values: MaybeRef<InferGetInput<"get_site_supervision_chart">>,
  opts?: { enabled?: MaybeRef<boolean> },
) {
  return useQuery({
    enabled: () => hasScope(Scope.enum["read:supervision"]) && toValue(opts?.enabled ?? true),
    queryKey: ["supervision", "site", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_site_supervision_chart")
      const data = await useApiClient().get_site_supervision_chart(config)
      return data as DataFrame
    },
  })
}

// __________ Invoice __________
//
// The supply invoices tab sums the EE redevance billed by the site's invoices, per status
const INVOICE_LIST_KEYS = [["invoices"], ["documents", "supplyInvoices"]]

export function useInvoicesQuery(
  values: MaybeRef<InferGetInput<"get_invoices_filtered_by_model">>,
) {
  return useQuery({
    enabled: hasScope(Scope.enum["read:invoice"]),
    queryKey: ["invoices", values],
    queryFn: async () => {
      const { body, config } = dispatchEndpointArgs(values, "get_invoices_filtered_by_model")
      return await useApiClient().get_invoices_filtered_by_model(body, config)
    },
    cacheTime: gcTimesMap.long,
  })
}

export function useInvoicingSummaryQuery(
  values: MaybeRef<InferGetInput<"get_invoices_summary_filtered_by_model">>,
) {
  return useQuery({
    enabled: hasScope(Scope.enum["read:invoice"]),
    queryKey: ["invoices", values],
    queryFn: async () => {
      const { body, config } = dispatchEndpointArgs(
        values,
        "get_invoices_summary_filtered_by_model",
      )
      return await useApiClient().get_invoices_summary_filtered_by_model(body, config)
    },
  })
}

export function useInvoicesToRecoverQuery(
  values: MaybeRef<InferGetInput<"get_invoices_to_recover">>,
) {
  return useQuery({
    enabled: hasScope(Scope.enum["read:invoice"]),
    queryKey: ["invoices", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_invoices_to_recover")
      return await useApiClient().get_invoices_to_recover(config)
    },
  })
}

export function useComputeInvoiceMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"compute_invoice_from_model">[]) => {
      const client = useApiClient()
      const promises = values.map((data) => {
        const { body, config } = dispatchEndpointArgs(data, "compute_invoice_from_model")
        // The mutation error notification already carries the backend message
        return client.compute_invoice_from_model(body, { ...config, errorNotification: false })
      })

      await Promise.all(promises)
    },
    ...getMutationOptions(MutationTypes.enum.computeInvoice, {
      listKey: INVOICE_LIST_KEYS,
      atomicKey: "invoice",
      notif: { atEnter: NotifLevel.enum.info },
      // A detected counter reset fails the compute but stores the estimated meter
      // change on the invoice, which has to show up
      invalidateOnError: true,
    }),
    ...trackMutation<InferMutateInput<"compute_invoice_from_model">[]>("invoice_computed", (v) => ({
      invoice_count: v.length,
    })),
  })
}

export function useCreatePaymentScheduleMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"create_payment_schedule_from_model">[]) => {
      const client = useApiClient()
      const promises = values.map((data) => {
        const { body, config } = dispatchEndpointArgs(data, "create_payment_schedule_from_model")
        return client.create_payment_schedule_from_model(body, config)
      })

      await Promise.all(promises)
    },
    ...getMutationOptions(MutationTypes.enum.createPaymentSchedule, {
      listKey: "invoices",
      atomicKey: "invoice",
      notif: { atEnter: NotifLevel.enum.info },
    }),
    ...trackMutation<unknown[]>("payment_schedule_created", (v) => ({ invoice_count: v.length })),
  })
}

export function useCreateRegulationMutation() {
  // `uuid` is the contract's: the invoice lists and the redevance to regularise are refreshed
  const options = getMutationOptions(MutationTypes.enum.createRegulation, {
    listKey: INVOICE_LIST_KEYS,
  })
  const { t } = useI18n()
  return useMutation({
    mutationFn: async (values: InferMutateInput<"create_regulation_invoice_for_contract">) => {
      const { body, config } = dispatchEndpointArgs(
        values,
        "create_regulation_invoice_for_contract",
      )
      return await useApiClient().create_regulation_invoice_for_contract(body, config)
    },
    ...options,
    onSuccess: (invoices, variables, context) => {
      if (invoices.length) return options.onSuccess(invoices, variables, context)
      useNotifStore().addNotification({
        title: t("components.queries.creating_regulation"),
        text: t("invoice.modal.nothing_to_regularise"),
        level: NotifLevel.enum.info,
      })
    },
    ...trackMutation<InferMutateInput<"create_regulation_invoice_for_contract">>(
      "regulation_created",
      (v) => ({ month_count: dt.differenceInCalendarMonths(v.end, v.start) }),
    ),
  })
}

export function useInvoiceQuery(uuid: MaybeRefOrGetter<string>, enabled: MaybeRef<boolean> = true) {
  return useQuery({
    enabled: computed(() => hasScope(Scope.enum["read:invoice"]) && toValue(enabled)),
    queryKey: computed(() => ["invoice", toValue(uuid)]),
    queryFn: async () => {
      return await useApiClient().get_invoice({ params: { uuid: toValue(uuid) } })
    },
  })
}

export function useInvoiceSubmitQuery() {
  return useMutation({
    mutationFn: async (data: { uuid: InvoiceSchemaShort["uuid"] }) =>
      useApiClient().submit_invoice_from_model(
        {},
        { params: { model: "invoice", uuid: data.uuid } },
      ),
    ...getMutationOptions(MutationTypes.enum.submitInvoice, {
      listKey: INVOICE_LIST_KEYS,
      atomicKey: "invoice",
      notif: { atEnter: NotifLevel.enum.info },
    }),
    ...trackMutation("invoice_submitted", () => ({ invoice_count: 1 })),
  })
}

export function useBulkInvoiceSubmitQuery() {
  return useMutation({
    mutationFn: async (data: { uuids: InvoiceSchemaShort["uuid"][] }) => {
      const client = useApiClient()
      await Promise.all(
        data.uuids.map((uuid) =>
          client.submit_invoice_from_model({}, { params: { model: "invoice", uuid } }),
        ),
      )
    },
    ...getMutationOptions(MutationTypes.enum.submitInvoice, {
      listKey: INVOICE_LIST_KEYS,
      atomicKey: "invoice",
      notif: { atEnter: NotifLevel.enum.info },
    }),
    ...trackMutation<{ uuids: string[] }>("invoice_submitted", (v) => ({
      invoice_count: v.uuids.length,
    })),
  })
}

export function useInvoicePublishQuery() {
  return useMutation({
    mutationFn: async (data: { uuid: InvoiceSchemaShort["uuid"]; as_draft: boolean }) =>
      useApiClient().publish_invoice_from_model(
        { as_draft: data.as_draft },
        { params: { model: "invoice", uuid: data.uuid } },
      ),
    ...getMutationOptions(MutationTypes.enum.publishInvoice, {
      listKey: INVOICE_LIST_KEYS,
      atomicKey: "invoice",
      notif: { atEnter: NotifLevel.enum.info },
    }),
    ...trackMutation<{ uuid: string; as_draft: boolean }>("invoice_published", (v) => ({
      invoice_count: 1,
      as_draft: v.as_draft,
    })),
  })
}

export function useBulkInvoicePublishQuery() {
  return useMutation({
    mutationFn: async (data: { uuids: InvoiceSchemaShort["uuid"][]; as_draft: boolean }) => {
      const client = useApiClient()
      // No bulk publish endpoint: one call per invoice, drafts included.
      await Promise.all(
        data.uuids.map((uuid) =>
          client.publish_invoice_from_model(
            { as_draft: data.as_draft },
            { params: { model: "invoice", uuid } },
          ),
        ),
      )
    },
    ...getMutationOptions(MutationTypes.enum.publishInvoice, {
      listKey: INVOICE_LIST_KEYS,
      atomicKey: "invoice",
      notif: { atEnter: NotifLevel.enum.info },
    }),
    ...trackMutation<{ uuids: string[]; as_draft: boolean }>("invoice_published", (v) => ({
      invoice_count: v.uuids.length,
      as_draft: v.as_draft,
    })),
  })
}

export function useCancelInvoiceMutateQuery() {
  return useMutation({
    mutationFn: async (values: {
      uuids: string[]
      cancel_action: InvoiceCancelActions
      is_internal_credit_note?: boolean
    }) => {
      const client = useApiClient()
      await Promise.all(
        values.uuids.map((uuid) =>
          client.cancel_invoice(undefined, {
            queries: {
              cancel_action: values.cancel_action,
              is_internal_credit_note: values.is_internal_credit_note ?? false,
            },
            params: { uuid },
          }),
        ),
      )
    },
    ...getMutationOptions(MutationTypes.enum.cancelInvoice, {
      listKey: INVOICE_LIST_KEYS,
      atomicKey: "invoice",
    }),
    ...trackMutation<{
      uuids: string[]
      cancel_action: InvoiceCancelActions
      is_internal_credit_note?: boolean
    }>("invoice_cancelled", (v) => ({
      invoice_count: v.uuids.length,
      cancel_action: v.cancel_action,
      is_internal_credit_note: v.is_internal_credit_note ?? false,
    })),
  })
}

export function useSetInvoiceStatusOrActionQuery() {
  return useMutation({
    mutationFn: async (
      values: {
        uuid: string
        status_or_action:
          | "submitted"
          | "paid"
          | "computed"
          | "accept"
          | "reject"
          | "waiting_for_publication"
      }[],
    ) => {
      const client = useApiClient()
      await Promise.all(
        values.map((params) => client.set_invoice_status_or_action(undefined, { params })),
      )
    },
    ...getMutationOptions(MutationTypes.enum.setInvoiceDefinitiveStatus, {
      listKey: INVOICE_LIST_KEYS,
      atomicKey: "invoice",
    }),
    // Callers always apply a single status to the whole batch.
    ...trackMutation<{ status_or_action: string }[]>("invoice_status_updated", (v) => ({
      invoice_count: v.length,
      status_or_action: v[0]?.status_or_action,
    })),
  })
}

export function useSetInvoiceAccountedQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"set_invoice_accounted">) => {
      const { body, config } = dispatchEndpointArgs(values, "set_invoice_accounted")
      return await useApiClient().set_invoice_accounted(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.setInvoiceAccounted, {
      listKey: "invoices",
      atomicKey: "invoice",
    }),
    ...trackMutation<InferMutateInput<"set_invoice_accounted">>("invoice_accounted", (v) => ({
      is_accounted: v.is_accounted ?? true,
    })),
  })
}

export function useInvoiceDownloadPDF(
  uuid: string,
  has_pdf: MaybeRef<boolean>,
  i18n: { t: (value: string) => string },
) {
  return useQuery({
    enabled: () => hasScope(Scope.enum["read:invoice"]) && toValue(has_pdf),
    queryKey: ["invoice", uuid, "document"],
    queryFn: async () => {
      const hasPdf = toValue(has_pdf)
      if (!hasPdf) {
        return new Blob([], { type: "application/pdf" })
      }
      try {
        const axios = useAxiosInstance()
        const resp = await axios.get(`api/invoices/${uuid}/document`, { responseType: "blob" })
        return resp.data as Blob
      } catch (err) {
        useNotifStore().addNotification({
          title: i18n.t("notification.missing_pdf.title"),
          level: NotifLevel.enum.alert,
          text: i18n.t("notification.missing_pdf.message"),
        })
        throw err
      }
    },
  })
}

export function useUpdateInvoiceLineMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"update_invoice_line">) => {
      const { body, config } = dispatchEndpointArgs(values, "update_invoice_line")
      return await useApiClient().update_invoice_line(body, config)
    },
    ...getMutationOptions(MutationTypes.enum.updateInvoiceLine, {
      listKey: [...INVOICE_LIST_KEYS, ["invoice"]],
    }),
    ...trackMutation("invoice_line_updated"),
  })
}

export function useSetInvoiceExtraMutateQuery() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"update_invoice">) => {
      // Bypass dispatchEndpointArgs: the `extra` union of two all-optional
      // object schemas would parse as its first member and strip the
      // load-profile energies. The endpoint is registered without request
      // parsing (see useApiClient), so the body goes through as-is.
      return await useApiClient().update_invoice(
        { extra: values.extra },
        { params: { uuid: values.uuid } },
      )
    },
    ...getMutationOptions(MutationTypes.enum.setInvoiceExtra, {
      listKey: [["invoices"], ["invoice"]],
    }),
    ...trackMutation("invoice_extra_set"),
  })
}

export function usePublishInvoicingRequestMutateQuery() {
  return useMutation({
    // Generates the invoicing request, publishes the
    // waiting_for_publication excel invoices in [start, end) (both optional), then
    // downloads the generated xlsx (stored as a Document on the assetmanager).
    mutationFn: async (data: { uuid: string; start?: Date; end?: Date }) => {
      const client = useApiClient()
      const document = await client.publish_invoicing_request_document(undefined, {
        params: { uuid: data.uuid },
        queries: { start: data.start, end: data.end },
      })
      const axios = useAxiosInstance()
      const resp = await axios.get(`api/document/${document.uuid}/content`, {
        responseType: "blob",
      })
      await dispatchFileResponses([resp])
    },
    ...getMutationOptions(MutationTypes.enum.publish_invoicing_request, {
      listKey: [["invoices"], ["invoice"]],
    }),
    ...trackMutation<{ start?: Date; end?: Date }>("invoicing_request_published", (v) => ({
      has_date_range: R.isNonNullish(v.start) || R.isNonNullish(v.end),
    })),
  })
}

// __________ Commissioning __________
//
export function useCommissioningQuery(
  values: MaybeRef<InferGetInput<"get_sites_for_commissioning">>,
) {
  return useQuery({
    queryKey: ["commissioning", values],
    queryFn: async () => {
      const { config } = dispatchEndpointArgs(values, "get_sites_for_commissioning")
      return await useApiClient().get_sites_for_commissioning(config)
    },
  })
}

export function useSupplyInvoiceUpdateMutation() {
  return useMutation({
    mutationFn: async (values: InferMutateInput<"update_supply_invoice">) => {
      const { body, config } = dispatchEndpointArgs(values, "update_supply_invoice")
      return await useApiClient().update_supply_invoice(body, config)
    },
    // The site's supply invoices query is keyed under "documents"
    ...getMutationOptions(MutationTypes.enum.updateSupplyInvoice, { listKey: "documents" }),
    ...trackMutation<InferMutateInput<"update_supply_invoice">>("supply_invoice_updated", (v) => ({
      excluded_reason: v.excluded_reason,
      line_count: v.lines?.length ?? 0,
    })),
  })
}

export function useSiteSupplyInvoicesQuery(site_uuid: string) {
  return useQuery({
    queryKey: ["documents", "supplyInvoices", site_uuid],
    queryFn: async () =>
      await useApiClient().get_supply_invoices({ params: { model: "site", uuid: site_uuid } }),
  })
}

export function useSiteBudgetsQuery(site_uuid: string) {
  return useQuery({
    queryKey: ["site", site_uuid, "budgets"],
    queryFn: async () => await useApiClient().get_site_budgets({ params: { uuid: site_uuid } }),
  })
}
